/**
 * AisleZoneLayer - Owner-only aisle / walkway exclusion visualization
 * Elvis Aloha Collection - Map Editor
 *
 * Renders each aisle_zone polygon as a translucent red exclusion mask + label,
 * using native chart-image coordinates inside a full-image SVG overlay attached
 * to the OpenSeadragon tiled image (so it stays glued to the chart on pan/zoom).
 *
 * This is an OWNER EDITING AID only. It never renders on the public map, and it
 * does not modify the underlying historic chart image. pointer-events are OFF so
 * it never blocks map panning or seat interaction.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import OpenSeadragon from 'openseadragon';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const AisleZoneLayer = ({ viewer, chartId, chartDimensions, authToken, visible = true }) => {
  const [aisles, setAisles] = useState([]);
  const overlayRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!chartId) return;
    const controller = new AbortController();
    const load = async () => {
      try {
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const res = await fetch(`${API_BASE}/api/seat-map/aisle-zones/${chartId}`, { headers, signal: controller.signal });
        const data = res.ok ? await res.json() : { aisles: [] };
        setAisles(data.aisles || []);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('AisleZoneLayer fetch error:', err);
      }
    };
    load();
    return () => controller.abort();
  }, [chartId, authToken]);

  const renderPolygons = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    aisles.forEach((az) => {
      const poly = az.native_polygon || [];
      if (poly.length < 3) return;
      const pts = poly.map(p => `${p[0]},${p[1]}`).join(' ');

      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      polygon.setAttribute('points', pts);
      polygon.setAttribute('fill', 'rgba(216, 60, 44, 0.28)');
      polygon.setAttribute('stroke', '#D83C2C');
      polygon.setAttribute('stroke-width', '3');
      polygon.setAttribute('stroke-dasharray', '10 6');
      polygon.style.pointerEvents = 'none';
      svg.appendChild(polygon);

      // Centroid for the label
      const cx = poly.reduce((a, p) => a + p[0], 0) / poly.length;
      const cy = poly.reduce((a, p) => a + p[1], 0) / poly.length;
      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      label.setAttribute('x', cx);
      label.setAttribute('y', cy);
      label.setAttribute('fill', '#7A0F06');
      label.setAttribute('font-size', '22');
      label.setAttribute('font-weight', '700');
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('transform', `rotate(90, ${cx}, ${cy})`);
      label.style.pointerEvents = 'none';
      label.textContent = az.label || 'Aisle';
      svg.appendChild(label);
    });
  }, [aisles]);

  const setupOverlay = useCallback(() => {
    if (!viewer || !chartDimensions?.width || !chartDimensions?.height) return;
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;

    if (overlayRef.current) {
      try { viewer.removeOverlay(overlayRef.current); } catch (e) { /* noop */ }
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${chartDimensions.width} ${chartDimensions.height}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none';
    svg.setAttribute('data-testid', 'aisle-zone-overlay-svg');
    svgRef.current = svg;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.pointerEvents = 'none';
    container.appendChild(svg);
    overlayRef.current = container;

    const imageRect = tiledImage.imageToViewportRectangle(0, 0, chartDimensions.width, chartDimensions.height);
    viewer.addOverlay({
      element: container,
      location: new OpenSeadragon.Rect(imageRect.x, imageRect.y, imageRect.width, imageRect.height),
    });

    renderPolygons();
  }, [viewer, chartDimensions, aisles, renderPolygons]);

  useEffect(() => {
    if (!viewer) return;
    const handleOpen = () => {
      if (viewer.world.getItemAt(0)) setupOverlay();
    };
    if (viewer.isOpen && viewer.isOpen()) handleOpen();
    viewer.addHandler('open', handleOpen);
    return () => {
      viewer.removeHandler('open', handleOpen);
      if (overlayRef.current) {
        try { viewer.removeOverlay(overlayRef.current); } catch (e) { /* noop */ }
      }
    };
  }, [viewer, setupOverlay]);

  useEffect(() => { renderPolygons(); }, [renderPolygons, aisles]);
  
  // Toggle overlay visibility without destroying it
  useEffect(() => {
    if (svgRef.current) svgRef.current.style.display = visible ? 'block' : 'none';
  }, [visible, aisles]);

  return null;
};

export default AisleZoneLayer;
